import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getPhotoByDate, API_BASE_URL } from '../services/api';
import { formatDateDisplay, getRatingColor, getRatingLabel, isPhotoDraft } from '../utils/dateUtils';

const PhotoViewPage = () => {
  const { date: dateParam } = useParams();
  const navigate = useNavigate();
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPhoto = async () => {
      try {
        setLoading(true);
        setError(null);
        // Date should already be in YYYY-MM-DD format (URL-safe)
        const dateString = dateParam;
        console.log('Fetching photo for date:', dateString);
        const data = await getPhotoByDate(dateString);
        setPhoto(data);
      } catch (err) {
        console.error('Error fetching photo:', err);
        if (err.response?.status === 404) {
          setError('Photo not found for this date');
        } else {
          setError(`Failed to load photo: ${err.message || 'Unknown error'}`);
        }
      } finally {
        setLoading(false);
      }
    };

    if (dateParam) {
      fetchPhoto();
    } else {
      setError('No date specified');
      setLoading(false);
    }
  }, [dateParam]);

  const getPhotoUrl = (photo) => {
    if (!photo.file_path) return null;
    if (photo.file_path.startsWith('http')) {
      return photo.file_path;
    }
    return `${API_BASE_URL}${photo.file_path}`;
  };


  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ pt: { xs: 10, sm: 12 }, mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ pt: { xs: 10, sm: 12 }, mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon /> Go Back
        </IconButton>
      </Container>
    );
  }

  if (!photo) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ pt: { xs: 10, sm: 12 }, mt: 4, mb: 4, px: { xs: 1, sm: 2 } }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
          {formatDateDisplay(photo.date)}
        </Typography>
        {isPhotoDraft(photo) && (
          <Chip label="Draft" color="default" sx={{ fontWeight: 'bold' }} />
        )}
        {photo.rating && (
          <Chip
            label={getRatingLabel(photo.rating)}
            sx={{
              bgcolor: getRatingColor(photo.rating),
              color: '#fff',
              fontWeight: 'bold',
              px: 1,
            }}
          />
        )}
      </Box>

      {getPhotoUrl(photo) ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            bgcolor: '#fafafa',
            borderRadius: 1,
            p: 2,
          }}
        >
          <Box
            component="img"
            src={getPhotoUrl(photo)}
            alt={`Photo from ${photo.date}`}
            sx={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 1,
            }}
          />
        </Box>
      ) : (
        <Paper elevation={0} sx={{ p: 3, bgcolor: '#fafafa', borderRadius: 1, textAlign: 'center' }}>
          <Typography color="text.secondary">No photo for this day yet.</Typography>
        </Paper>
      )}

      {photo.description && (
        <Paper elevation={0} sx={{ mt: 3, p: 2, bgcolor: '#fafafa', borderRadius: 1 }}>
          <Typography variant="body1">
            {photo.description}
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default PhotoViewPage;

